import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    InfoCircleOutlined,
} from '@ant-design/icons';
import { Modal, Timeline, Spin } from 'antd';
import { useEffect, useState } from 'react';
import synclogService from '../../../../client-lib/api/synclog-service';
import { toastText } from '../../../../components/global/toast';
import './index.css';
import { ISynclogHistory } from './types';

type Props = {
    open: boolean;
    onCancel: (isEdit?: boolean) => void;
    synclogId: string;
};

export const SynclogHistoryModal = ({ open, onCancel, synclogId }: Props) => {
    const [loading, setLoading] = useState(false);
    const [syncHistory, setSyncHistory] = useState<ISynclogHistory[]>([]);

    useEffect(() => {
        if (open && synclogId && synclogId.trim() !== '') {
            fetchSyncHistory();
        } else if (open && (!synclogId || synclogId.trim() === '')) {
            console.warn('Modal opened without valid synclogId:', synclogId);
            setSyncHistory([]);
            setLoading(false);
        }
    }, [open, synclogId]);

    const fetchSyncHistory = async () => {
        if (!synclogId?.trim()) {
            console.warn('fetchSyncHistory called without valid synclogId');
            setSyncHistory([]);
            return;
        }

        setLoading(true);
        try {
            const res = await synclogService.getB2CSynclogById(synclogId);
            const result = res?.result;

            // Extract history using the most expected location
            let rawHistory = result?.history;

            // Fallbacks for less common structures
            if (!rawHistory) {
                rawHistory =
                    result?.syncLog?.syncHistory ||
                    (res as any)?.syncHistory ||
                    (Array.isArray(result) ? result : null);

                // Handle single-key object containing array
                if (!rawHistory && typeof result === 'object') {
                    const key = Object.keys(result)[0];
                    if (key && Array.isArray(result[key])) {
                        rawHistory = result[key];
                    }
                }
            }

    const history = Array.isArray(rawHistory)
      ? rawHistory
      : rawHistory
        ? [rawHistory]
        : [];

    if (!history.length) {
      console.warn('No sync history found in response:', res);
    }

    setSyncHistory(history);
  } catch (error: any) {
    console.error('Error fetching sync history:', error);
    const msg =
      error?.message ??
      error?.errorDescription ??
      (typeof error === 'string' ? error : 'Failed to fetch sync history');
    toastText(msg, 'error');
    setSyncHistory([]);
  } finally {
    setLoading(false);
  }
};


    const transformSyncHistory = (history: ISynclogHistory[]) => {
        if (!history || !Array.isArray(history)) {
            return [];
        }

        return history.map((data: ISynclogHistory, index: number) => {
            if (!data) {
                return {
                    label: `Entry ${index + 1}`,
                    children: <p>Invalid data entry</p>,
                    dot: <CloseCircleOutlined style={{ fontSize: '16px', color: 'red' }} />
                };
            }

            const timestamp = data.timeStamp ? new Date(data.timeStamp).toLocaleString() : `Entry ${index + 1}`;
            const message = data.message || 'No message available';
            const status = data.status || 'unknown';

            return {
                label: timestamp,
                children: <p>{message}</p>,
                dot:
                    status === 'success' ? (
                        <CheckCircleOutlined
                            style={{ fontSize: '16px', color: 'green' }}
                        />
                    ) : status === 'pending' ? (
                        <InfoCircleOutlined
                            style={{ fontSize: '16px', color: 'orange' }}
                        />
                    ) : (
                        <CloseCircleOutlined
                            style={{ fontSize: '16px', color: 'red' }}
                        />
                    ),
            };
        });
    };

    return (
        <Modal
            open={open}
            footer={null}
            width={800}
            onCancel={() => onCancel()}
            title={'Sync History'}
            centered
            className="sync-history-model"
        >
            {loading ? (
                <div style={{ textAlign: 'center', padding: '50px' }}>
                    <Spin size="large" />
                </div>
            ) : syncHistory.length > 0 ? (
                <Timeline
                    mode={'left'}
                    items={transformSyncHistory(syncHistory)}
                    className="syncHistoryItem"
                />
            ) : (
                <div style={{ textAlign: 'center', padding: '50px', color: '#999' }}>
                    <p>No sync history available for this record.</p>
                </div>
            )}
        </Modal>
    );
};
